import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { range } from 'lodash';
import moment from 'moment';
import { ReactComponent as DropdownIcon } from 'assets/icons/chevron-down.svg';
import useClinic from 'hooks/useClinic';

interface SelectTimeProps {
  type: 'start' | 'end';
  day: string;
  value: string;
  onChange: (day: string, value: string, type: string) => void;
  start?: string;
  end?: string;
  isInEditModal?: boolean;
}

const SelectTime: FC<SelectTimeProps> = ({
  value,
  type,
  day,
  onChange,
  start,
  end,
  isInEditModal,
}) => {
  const { data } = useClinic();

  const options = useMemo(() => {
    const duration = data?.timeInterval ? data.timeInterval : 30;

    return range(0, 24, 1)
      .map((hour) => {
        return range(0, 60, duration)
          .map((minute) => {
            const momentObj = moment({
              hour,
              minute,
            });
            return {
              label: momentObj.format('hh:mm A'),
              value: momentObj.format('HH:mm:ss'),
            };
          })
          .flat();
      })
      .flat();
  }, [data?.timeInterval]);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const [query, setQuery] = useState('');

  const selectedOption = options.find((option) => option.value === value)!;

  const getOptions = () => {
    let filterdOptions = [...options];
    if (start) {
      filterdOptions = filterdOptions.filter((option) =>
        type === 'start'
          ? moment(option.value, 'HH:mm:ss').isSameOrAfter(
              moment(start, 'HH:mm:ss')
            )
          : moment(option.value, 'HH:mm:ss').isAfter(moment(start, 'HH:mm:ss'))
      );
    }
    if (end) {
      filterdOptions = filterdOptions.filter((option) =>
        type === 'end'
          ? moment(option.value, 'HH:mm:ss').isSameOrBefore(
              moment(end, 'HH:mm:ss')
            )
          : moment(option.value, 'HH:mm:ss').isBefore(moment(end, 'HH:mm:ss'))
      );
    }

    return filterdOptions;
  };

  useEffect(() => {
    const position = buttonRef.current!.getBoundingClientRect();
    const screenHeight = window.innerHeight;
    const listBoxHeight = 302;
    const elementHeight = position.top + 32;
    const isOverflow = elementHeight + listBoxHeight >= screenHeight;
    setIsOverflow(isOverflow);
  }, []);

  const filterOptions = () => {
    const options = getOptions();
    return query === ''
      ? options
      : options.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
  };

  return (
    <Combobox
      value={selectedOption}
      onChange={(option: { label: string; value: string }) => {
        onChange(day, option.value, type);
      }}
      as="div"
      className="text-12 leading-[1.8rem]"
    >
      {({ open }) => (
        <>
          <div className="relative">
            <Combobox.Input
              onChange={(e) => setQuery(e.target.value)}
              displayValue={() => selectedOption?.label || ''}
              className={`flex items-center relative rounded-[0.8rem] shadow-primary border-[0.5px] border-transparent text-12 leading-[1.8rem]
              ${open ? '!shadow-select-active !border-magenta' : '!border-transparent'}
              ${
                isInEditModal
                  ? 'w-12.3 h-3 justify-start pl-1.2'
                  : 'w-9 h-3.2 justify-center'
              }`}
            />
            <Combobox.Button
              ref={buttonRef}
              className="absolute inset-y-0 right-0 flex items-center justify-center pr-1"
              onClick={() => setQuery('')}
            >
              <DropdownIcon className="w-1.2 h-1.2" />
            </Combobox.Button>
          </div>

          <Combobox.Options
            className={`absolute mt-0.3 bg-white text-base flex flex-col gap-y-1.6 rounded-[1rem] shadow-primary max-h-[30.2rem] scrollbar overflow-auto z-[100] py-0.8 ${
              isInEditModal ? 'w-12.3' : 'w-9'
            } ${isOverflow ? '-translate-y-[calc(100%+4rem)]' : ''}`}
          >
            {filterOptions().map((option) => (
              <Combobox.Option
                key={option.label}
                value={option}
                className={({ active, selected }) =>
                  `cursor-pointer pl-0.8 ${
                    active || selected ? 'text-magenta' : ''
                  }`
                }
              >
                {option.label}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </>
      )}
    </Combobox>
  );
};

export default SelectTime;
